/**
 * Helpers for the per-parade-year SubKrewe dues config.
 *
 * Storage shape: `SubKrewe.subkrewe_dues_by_year` is a map from a parade
 * year (string, e.g. "2027") to a `SubkreweDuesConfig`. The feature is
 * opt-in — a missing entry or `enabled: false` means this SubKrewe does
 * not collect its own dues through the app.
 *
 * See docs/SubkreweDues.md for product intent.
 */

import { isRedshirt, isSpecialtyRedshirt } from '../types/memberType.js'
import type { SubKrewe, SubkreweDuesConfig } from '../types/subkrewe.js'
import { toDate } from './dates.js'

export interface MemberLike {
  member_type?: string
  subkrewe?: { specialty?: string[] } | null
}

/**
 * Resolve the dues config for a given parade year, or `undefined` when
 * absent. Does not coerce/fill defaults.
 */
export function getSubkreweDuesConfig(
  subkrewe: Pick<SubKrewe, 'subkrewe_dues_by_year'> | null | undefined,
  year: string | number | null | undefined,
): SubkreweDuesConfig | undefined {
  if (!subkrewe || year === null || year === undefined) return undefined
  const map = subkrewe.subkrewe_dues_by_year
  if (!map) return undefined
  return map[String(year)]
}

/**
 * The per-year configs as read from Firestore, with each `due_date` made a
 * Date. Firestore returns it as a Timestamp, which the dues screens neither
 * show nor keep: the captain's next save dropped it.
 */
export function duesConfigsWithDates(
  map: Record<string, SubkreweDuesConfig> | null | undefined,
): Record<string, SubkreweDuesConfig> | undefined {
  if (!map) return undefined
  return Object.fromEntries(Object.entries(map).map(([year, config]) => [
    year,
    config?.due_date ? { ...config, due_date: toDate(config.due_date) } : config,
  ]))
}

/**
 * Whether a given SubKrewe currently collects its own dues for the given
 * year (opt-in + not a specialty redshirt subkrewe).
 */
export function isSubkreweDuesEnabled(
  subkrewe: (Pick<SubKrewe, 'subkrewe_dues_by_year'> & { specialty?: string[] }) | null | undefined,
  year: string | number | null | undefined,
): boolean {
  if (!subkrewe) return false
  // Specialty redshirt subkrewes (Free-Range / Nerd Herders) do not
  // surface subkrewe dues UI — decided in docs/SubkreweDues.md.
  if (isSpecialtyRedshirt({ subkrewe: { specialty: subkrewe.specialty } })) return false
  const config = getSubkreweDuesConfig(subkrewe, year)
  return config?.enabled === true
}

/**
 * Whether subkrewe dues are applicable to this particular member for the
 * given year. Evaluates at read time so reassigning a redshirt to a
 * different subkrewe is handled naturally.
 */
export function isSubkreweDuesApplicable(
  member: MemberLike | null | undefined,
  subkrewe: (Pick<SubKrewe, 'subkrewe_dues_by_year'> & { specialty?: string[] }) | null | undefined,
  year: string | number | null | undefined,
): boolean {
  if (!member || !subkrewe) return false
  if (!isSubkreweDuesEnabled(subkrewe, year)) return false
  const config = getSubkreweDuesConfig(subkrewe, year)
  if (!config) return false
  // Lifetime / support / regular / captain members: always applicable.
  // Redshirts: only when `charge_redshirts` is true.
  if (isRedshirt(member)) return config.charge_redshirts === true
  return true
}

/**
 * Stable composite key for indexing entries in client state.
 */
export function subkreweDuesEntryKey(subkreweId: string, year: string | number, memberId: string): string {
  return `${subkreweId}__${String(year)}__${memberId}`
}
